import React, { ReactNode } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { AirportCodes } from '../../../../types/airports';
import { Color, airportsColorTheme } from '../../../../styles/Color';
import { ArrowForwardIcon, Css, ITheme, Responsive } from '../../../../ui';
import { RouterLink } from '../../link/RouterLink';
import { useWindowSize } from '../../../../hooks/useWindowSize';

export enum GatewayCardType {
	Primary = 'primary',
	Secondary = 'secondary',
}

interface IGatewayCardProps {
	type: GatewayCardType;
	link: {
		id: string;
		code?: AirportCodes;
		label: string;
		icon?: ReactNode;
		href: string;
	};
}

export default function GatewayCard({ type, link }: IGatewayCardProps) {
	const isMobileXL = Responsive.useMatchesBreakpoint('mobileXL');
	const { id, code, label, icon, href } = link;

	const { windowHeight } = useWindowSize();

	return (
		<StyledCardLink
			href={href}
			className={id}
			type={type}
			code={code}
			windowHeight={windowHeight}
		>
			{!icon && (
				<AirportCode type={type} code={code} className="notranslate">
					{code}
				</AirportCode>
			)}
			{icon && <LinkIcon type={type}>{icon}</LinkIcon>}
			<CardBottomWrapper>
				<CardLabel>{label}</CardLabel>
				{isMobileXL && type === GatewayCardType.Primary && code && (
					<MobileButton code={code}>
						<ArrowForwardIcon
							aria-label={`Go to ${code.toLocaleUpperCase()} ${label}`}
						/>
					</MobileButton>
				)}
			</CardBottomWrapper>
		</StyledCardLink>
	);
}

export const GATEWAY_CARD_BORDER_RADIUS = '16px';
export const MOBILE_GATEWAY_CARD_BORDER_RADIUS = '8px';

export const PRIMARY_CARD_SUBTRACTION_HEIGHT_DESKTOP = 395;
export const PRIMARY_CARD_HEIGHT_DESKTOP_XL = Css.pxToRem(444);
export const PRIMARY_CARD_HEIGHT_TABLET_XL = Css.pxToRem(370);
const SECONDARY_CARD_HEIGHT_MOBILE = Css.pxToRem(100);

const StyledCardLink = styled(RouterLink)<
	{
		type: GatewayCardType;
		code?: AirportCodes;
		windowHeight?: number;
	},
	ITheme
>`
	position: relative;
	border-radius: ${GATEWAY_CARD_BORDER_RADIUS};
	border: 1px solid ${Color.BorderCard};
	padding: ${Css.pxToRem(16)};
	text-decoration: none;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	transition: all 0.3s ease;
	cursor: pointer;
	user-select: none;

	&:hover {
		${({ code }) =>
			code
				? `box-shadow: ${airportsColorTheme[code].primary} -8px 8px;`
				: `box-shadow: ${Color.Neutral3} -8px 8px;`}
		transform: translate(2px, -1px);
	}

	&:focus-visible {
		transition: none;
		outline: 2px solid ${Color.OnBackground};

		@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
			outline: 2px solid transparent;
		}
	}

	&:active {
		transform: translate(-1px, 1px);
		box-shadow: none;
	}

	${({ type, code }) =>
		type === GatewayCardType.Primary &&
		code &&
		css`
			::after {
				content: '';
				position: absolute;
				top: 0;
				right: 0;
				height: 40px;
				width: 40px;
				background: linear-gradient(
					to bottom left,
					${airportsColorTheme[code].primary} 50%,
					transparent 50%
				);
				border-top-right-radius: 15px;

				@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
					height: 32px;
					width: 32px;
					border-top-right-radius: 7px;
				}
			}
		`}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		border-radius: ${MOBILE_GATEWAY_CARD_BORDER_RADIUS};
		padding: ${({ type }) =>
			type === GatewayCardType.Primary
				? `${Css.pxToRem(16)} ${Css.pxToRem(10)} ${Css.pxToRem(
						10,
				  )} ${Css.pxToRem(16)}`
				: Css.pxToRem(12)};

		&:hover {
			box-shadow: none;
			border-radius: ${MOBILE_GATEWAY_CARD_BORDER_RADIUS};
		}
	}

	&.iah {
		grid-area: iah;
		max-height: ${PRIMARY_CARD_HEIGHT_DESKTOP_XL};
		background-color: ${airportsColorTheme.iah.primaryLight};

		@media (${Responsive.getMediaQueryForBreakpoint('desktop')}) {
			height: ${({ windowHeight }) =>
				windowHeight
					? `calc(${Css.pxToRem(windowHeight)} - ${Css.pxToRem(
							PRIMARY_CARD_SUBTRACTION_HEIGHT_DESKTOP,
					  )})`
					: 'auto'};
		}

		@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
			height: ${PRIMARY_CARD_HEIGHT_TABLET_XL};
		}

		@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
			height: auto;
		}
	}
	&.hou {
		grid-area: hou;
		max-height: ${PRIMARY_CARD_HEIGHT_DESKTOP_XL};
		background-color: ${airportsColorTheme.hou.primaryLight};

		@media (${Responsive.getMediaQueryForBreakpoint('desktop')}) {
			height: ${({ windowHeight }) =>
				windowHeight
					? `calc(${Css.pxToRem(windowHeight)} - ${Css.pxToRem(
							PRIMARY_CARD_SUBTRACTION_HEIGHT_DESKTOP,
					  )})`
					: 'auto'};
		}

		@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
			height: ${PRIMARY_CARD_HEIGHT_TABLET_XL};
		}

		@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
			height: auto;
		}
	}
	&.where-we-fly {
		grid-area: fly;
		background-color: ${Color.Neutral1};

		@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
			height: ${SECONDARY_CARD_HEIGHT_MOBILE};
		}
	}
	&.efd {
		grid-area: efd;
		background-color: ${airportsColorTheme.efd.primaryLight};

		@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
			height: ${SECONDARY_CARD_HEIGHT_MOBILE};
		}
	}
	&.spaceport {
		grid-area: spaceport;
		background-color: ${airportsColorTheme.spaceport.primaryLight};

		@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
			height: ${SECONDARY_CARD_HEIGHT_MOBILE};
		}
	}
`;

const AirportCode = styled.div<{
	type: GatewayCardType;
	code?: AirportCodes;
}>`
	text-align: ${({ type }) =>
		type === GatewayCardType.Primary ? 'left' : 'right'};
	text-transform: uppercase;
	font-weight: ${({ code }) => (code === AirportCodes.EFD ? 500 : 700)};
	font-size: ${({ type }) =>
		type === GatewayCardType.Primary ? Css.pxToRem(96) : Css.pxToRem(24)};
	color: ${({ code }) =>
		code === AirportCodes.IAH
			? airportsColorTheme.iah.secondary
			: code === AirportCodes.HOU
			? airportsColorTheme.hou.secondary
			: Color.OnBackground};

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		font-size: ${({ type }) =>
			type === GatewayCardType.Primary ? Css.pxToRem(72) : Css.pxToRem(24)};
	}
`;

const LinkIcon = styled.div<{ type: GatewayCardType }>`
	text-align: ${({ type }) =>
		type === GatewayCardType.Primary ? 'left' : 'right'};
`;

const CardBottomWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
`;

const MobileButton = styled.div<{ code: AirportCodes }, ITheme>`
	display: flex;
	justify-content: center;
	align-items: center;
	height: 40px;
	width: 40px;
	border-radius: ${(props) => props.theme.button.radius};
	background-color: ${({ code }) =>
		code === AirportCodes.IAH
			? airportsColorTheme.iah.secondary
			: airportsColorTheme.hou.secondary};

	svg {
		path {
			fill: ${Color.Neutral1};
		}
	}
`;

const CardLabel = styled.div`
	text-align: left;
	font-weight: 500;
	font-size: ${Css.pxToRem(24)};
	color: ${Color.OnBackground};
	width: fit-content;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		font-size: ${Css.pxToRem(16)};
	}
`;
